import React from 'react'
import App from 'next/app'
import Router from 'next/router'
import Head from 'next/head'
import cookies from 'next-cookies'
import NProgress from 'nprogress'
import { ToastProvider } from 'react-toast-notifications'
import { ReactQueryDevtools } from 'react-query/devtools'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Provider as JotaiProvider } from 'jotai'

import { appWithTranslation } from '~/i18n'
import { getData, postData } from '~/utils/api'
import { redirectTo } from '~/utils/serverHelpers'
import { sortDealersAsc } from '~/utils/mappers'
import { findDealer, isNoAuthPage, getDefaultRedirect } from '~/utils/helpers'
import { SessionContext } from '~/components'
import Cookies from 'js-cookie'
import NoDealerAccess from '~/components/NoDealerAccess'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import '~/styles/_app.css'
import '~/styles/overwrite.css'

// Loading Progress bar between page change
Router.events.on('routeChangeStart', () => NProgress.start())
Router.events.on('routeChangeComplete', () => NProgress.done())
Router.events.on('routeChangeError', () => NProgress.done())

// React-Query client
const queryClient = new QueryClient()

class Root extends App {
  constructor(props) {
    super(props)
    let carrierFilter = null
    this.state = {
      carrierFilter,
      carrierFilterOptions: [ 'FedEx', 'LOCKERS', 'UPS' ]
    }
  }

// @ts-ignore - something's wrong with getInitialProps
  static async getInitialProps({ Component, ctx }) {
    let pageProps = { namespacesRequired: [] }
    const c = cookies(ctx)
    const { pathname, req, res } = ctx

    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx)
    }

    //console.log('getInitialProps', process.env.NODE_ENV)
    const url = req
      ? `http://${process.env.NODE_ENV == "production" ? `127.0.0.1:${process.env.PORT || 8080}` : req.headers.host}/api/v1/session`
      : '/api/v1/session'
    const access_token = ((pathname === '/login' && req?.query?.labelUrl && req?.query?.success !== 'ok') ? null : c.access_token)
    const { type, auth, dealersObject, currentDealerId, status, userSettings } = await getData(url, {
      current_dealer_id: c.currentDealer,
      username: c.username,
      access_token,
      access_realm: c.access_realm,
    })

    let currentDealer = dealersObject?.[c.currentDealer];

    if (!currentDealer) {
      currentDealer = dealersObject?.[currentDealerId];
    }

    if (!currentDealer) {
      const firstDealerId = Object.keys(dealersObject || {})[0];

      if (firstDealerId) {
        const allowedDealers = dealersObject[firstDealerId]?.allowedDealers || [];
        const firstAllowedDealerId = allowedDealers[0];

        currentDealer = dealersObject[firstAllowedDealerId] || dealersObject[firstDealerId];
      }
    }

    if (status === 'NO_DEALER_ACCESS' || (status === 'OK' && !currentDealer)) {
      return {
        pageProps: {
          ...pageProps,
          namespacesRequired: [...(pageProps.namespacesRequired || []), 'common'],
          dealerAccessMissing: true,
        },
      }
    }

    if (status === "OK") {
      if (pathname === '/login' || pathname === '/') {
        if ((pathname === '/login' && req?.query?.labelUrl && req?.query?.success === 'ok')) {
          let user = auth.awsUserName || auth.name
          let url = req?.query?.labelUrl
          url += `&backUrl=${req?.query?.backUrl}&dealerId=${req?.query?.dealerId}&backTitle=${req?.query?.backTitle}&access_token=${req?.query?.access_token}&access_realm=${req?.query?.access_realm}&user=${encodeURIComponent(user)}`

          redirectTo(url, {
            res,
            status: 302,
          })
        } else {
          if (res) {
            let url = getDefaultRedirect(type)
            if (c?.href?.startsWith('/shop/')) {
              url = c.href
            }
            redirectTo(url, {
              res,
              status: 302,
            })
          }
        }
      }
      return {
        pageProps: {
          ...pageProps,
          sessionData: {
            auth,
            dealersObject
          },
          currentDealer,
          userSettings,
        },
      }
    } else {
        // Display the page if it's a page that doesn't require authorisation
        if (isNoAuthPage(pathname)) return { pageProps }
        // Else redirect to login page
        redirectTo('/login', { res, status: 302 })
        return { pageProps }
    }
  }

  componentDidMount() {
    const { pageProps } = this.props
    if (pageProps.locale === 'ru') {
      ;(function (w, d, u) {
        const s = d.createElement('script')
        s.async = true
        s.src = u + '?' + ((Date.now() / 60000) | 0)
        const h = d.getElementsByTagName('script')[0]
        h.parentNode.insertBefore(s, h)
      })(
        window,
        document,
        'https://cdn-ru.bitrix24.ru/b16422868/crm/site_button/loader_6_0dmgpz.js'
        //'https://cdn-ru.bitrix24.ru/b16422868/crm/site_button/loader_8_eombqh.js',
      )
    }

    // because 'react-select/creatable ^3.1.0' has bug
    try {
      if (typeof window !== 'undefined') {
        const carrierFilter = JSON.parse(window.localStorage.getItem('carrierFilter'))
        this.setState({
          carrierFilter,
          isReadyForLoadCharts: true,
        })
      }
    } catch(e) {}
  }

  render() {
    const { Component, pageProps } = this.props
    if (pageProps.dealerAccessMissing) return <NoDealerAccess />
    const { sessionData, currentDealer, userSettings } = pageProps
    this._userSettingsToLocalStorage(userSettings)
    return (
      <QueryClientProvider client={queryClient}>
        <JotaiProvider>
          <SessionContext.Provider
            value={{
              ...sessionData,
              currentDealer,
              userSettings,
            }}
          >
            <Head>
              <meta name="viewport" content="width=device-width, initial-scale=0.5" />
              <link rel="icon" type="image/png" href="/assets/favicon.png" />
            </Head>
            <ToastProvider
              autoDismiss
              autoDismissTimeout={5000}
              placement="top-center"
            >
              <Component
                {...pageProps }
                route={this.props.router.route}
                appState={this.state}
                setAppState={this._setAppState.bind(this)}
              />
            </ToastProvider>
          </SessionContext.Provider>
        </JotaiProvider>
        {process.env.NODE_ENV === 'development     ' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    )
  }

  private _setAppState(object) {
    this.setState(object)
  }

  private _userSettingsToLocalStorage(userSettings) {
    if (typeof window !== 'undefined') {
      Object.keys(userSettings || {}).forEach(key => {
        const value = userSettings[key]
        window.localStorage.setItem(key, JSON.stringify(value))
      })
    }
  }
}

export default appWithTranslation(Root)
